// Invoices on the matter: list with statuses, issue a new one, void an
// open one. Real matters only - a fixture matter has no Mongo id and no
// billing to speak of, so the card stays off (the DocWall rule).

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { downloadInvoicePdf, fetchCaseInvoices, type CaseInvoice } from "../api/caseInvoices";
import { InvoiceForm } from "./InvoiceForm";
import { RecordPaymentForm, recordedNote } from "./RecordPaymentForm";
import { VoidInvoiceButton } from "./VoidInvoiceButton";
import { useCurrentUser } from "../../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../../lib/roles";
import { useModuleOn } from "../../../api/attorneyConfig";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { cancelPlan, createPlan, fetchCasePlans } from "../api/paymentPlans";
import { fmtCents } from "../../../lib/money";
import { BILLING_OFF_COPY, day, INVOICE_PILL } from "../lib/billingLabels";

interface Props {
  /** Mongo _id of the Case; absent for fixture matters. */
  caseId?: string;
  /** The client's address, when the firm holds one - the invoice form only
      offers to email what it can actually reach. */
  clientEmail?: string;
}

export function BillingCard({ caseId, clientEmail }: Props) {
  const queryClient = useQueryClient();
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const billingOn = useModuleOn("billing");
  const [formOpen, setFormOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [recordForId, setRecordForId] = useState<string | null>(null);

  const [planOpen, setPlanOpen] = useState(false);
  const [planTotal, setPlanTotal] = useState("");
  const [planParts, setPlanParts] = useState("");
  const [planFirstDue, setPlanFirstDue] = useState("");
  const [planNotify, setPlanNotify] = useState(true);
  const [planError, setPlanError] = useState<string | null>(null);

  const canEmail = !!clientEmail?.trim();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["case-invoices", caseId ?? ""],
    queryFn: () => fetchCaseInvoices(caseId as string),
    enabled: !!caseId,
    staleTime: 30_000,
  });

  const plansQ = useQuery({
    queryKey: ["case-plans", caseId ?? ""],
    queryFn: () => fetchCasePlans(caseId as string),
    enabled: !!caseId,
    staleTime: 30_000,
  });

  const settle = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["case-invoices", caseId ?? ""] }),
      queryClient.invalidateQueries({ queryKey: ["case-plans", caseId ?? ""] }),
      // The firm's own plan ledger lists what is created here, and it
      // holds its answer for a minute.
      queryClient.invalidateQueries({ queryKey: ["billing"] }),
    ]);

  const planMut = useMutation({
    mutationFn: (input: Parameters<typeof createPlan>[1]) => createPlan(caseId as string, input),
    onSuccess: async (plan) => {
      setPlanOpen(false);
      setPlanTotal("");
      setPlanParts("");
      setPlanFirstDue("");
      setPlanNotify(true);
      setPlanError(null);
      setNote(
        `Payment plan created: ${fmtCents(plan.totalCents)} in ${plan.installments.length} monthly parts.`,
      );
      await settle();
    },
    onError: (err) =>
      setPlanError(err instanceof Error ? err.message : "Could not create the plan."),
  });

  const cancelPlanMut = useMutation({
    mutationFn: (planId: string) => cancelPlan(planId),
    onSuccess: async () => {
      setActionError(null);
      setNote("The payment plan was cancelled. Issued invoices are unaffected.");
      await settle();
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : "Could not cancel it."),
  });

  if (!caseId) return null;

  // A fresh outcome replaces both lines - a stale PDF error must not sit
  // above a new payment confirmation.
  const savePdf = async (inv: CaseInvoice) => {
    setNote(null);
    setActionError(null);
    try {
      await downloadInvoicePdf(inv._id, inv.number);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not download the PDF.");
    }
  };

  const submitPlan = () => {
    const dollars = Number(planTotal);
    const parts = Number(planParts);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setPlanError("Enter the total in dollars.");
      return;
    }
    if (!Number.isInteger(parts) || parts < 2) {
      setPlanError("A plan needs at least two monthly parts.");
      return;
    }
    if (!planFirstDue) {
      setPlanError("Pick the first due date.");
      return;
    }
    planMut.mutate({
      totalCents: Math.round(dollars * 100),
      installmentCount: parts,
      firstDueDate: planFirstDue,
      notifyClient: canEmail && planNotify,
    });
  };

  const invoices = data ?? [];
  const activePlan = (plansQ.data ?? []).find((p) => p.status === "active") ?? null;
  // A form opened before the identity answered goes with its toggle.
  const formShown = billingOn && formOpen;
  const planShown = billingOn && planOpen && !activePlan;

  return (
    <section className="cc-card" style={{ marginTop: 14 }}>
      <div className="cc-card-head">
        <div>
          <div className="cc-card-title">Billing</div>
          <div className="cc-card-sub">
            {invoices.length > 0
              ? `${invoices.length} invoice${invoices.length === 1 ? "" : "s"}`
              : "Nothing billed yet"}
          </div>
        </div>
        <div className="actions">
          {billingOn && !activePlan && (
            <button
              type="button"
              className="cc-link-btn"
              onClick={() => {
                // One form at a time: two open panels ask the same matter
                // to be billed twice over, in two different ways.
                setPlanOpen((v) => !v);
                setFormOpen(false);
                setPlanError(null);
              }}
            >
              {planOpen ? "Cancel" : "New plan"}
            </button>
          )}
          {billingOn && (
            <button
              type="button"
              className="cc-link-btn"
              onClick={() => {
                setFormOpen((v) => !v);
                setPlanOpen(false);
              }}
            >
              {formOpen ? "Cancel" : "New invoice"}
            </button>
          )}
        </div>
      </div>

      {note && <div className="cc-card-note">{note}</div>}
      {actionError && (
        <div role="alert" className="cc-card-note bad">
          {actionError}
        </div>
      )}

      {formShown && (
        <InvoiceForm
          caseId={caseId}
          clientEmail={clientEmail}
          onIssued={(invoice, emailSent) => {
            setFormOpen(false);
            setNote(
              emailSent
                ? `${invoice.number ?? "Invoice"} issued and emailed to the client.`
                : `${invoice.number ?? "Invoice"} issued. The client was not emailed.`,
            );
            void settle();
          }}
        />
      )}

      {activePlan && (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 10,
            padding: "7px 0",
            borderBottom: "1px solid var(--line)",
            fontSize: 13,
          }}
        >
          <span style={{ flex: 1 }}>
            Payment plan: <span className="tnum">{fmtCents(activePlan.totalCents)}</span> in{" "}
            {activePlan.installments.length} monthly parts
          </span>
          <span className="tnum" style={{ fontSize: 12, color: "var(--muted)" }}>
            {activePlan.paidCount} of {activePlan.installments.length} paid
          </span>
          {billingOn && (
            <button
              type="button"
              className="cc-link-btn"
              disabled={cancelPlanMut.isPending}
              onClick={() => cancelPlanMut.mutate(activePlan._id)}
            >
              Cancel plan
            </button>
          )}
        </div>
      )}

      {planShown && (
        <div className="cc-form-panel">
          <div className="cc-form-row">
            <label className="cc-form-field" style={{ width: 150 }}>
              <span className="cc-form-label">Total</span>
              <span className="cc-money-input">
                <span aria-hidden="true">$</span>
                <input
                  className="cc-field tnum"
                  aria-label="Plan total in dollars"
                  placeholder="0.00"
                  value={planTotal}
                  onChange={(e) => setPlanTotal(e.target.value)}
                  inputMode="decimal"
                />
              </span>
            </label>

            <label className="cc-form-field" style={{ width: 110 }}>
              <span className="cc-form-label">Monthly parts</span>
              <input
                className="cc-field tnum"
                aria-label="Number of monthly parts"
                placeholder="3"
                value={planParts}
                onChange={(e) => setPlanParts(e.target.value)}
                inputMode="numeric"
              />
            </label>

            <label className="cc-form-field" style={{ width: 165 }}>
              <span className="cc-form-label">First due date</span>
              <input
                className="cc-field"
                aria-label="First due date"
                type="date"
                value={planFirstDue}
                onChange={(e) => setPlanFirstDue(e.target.value)}
              />
            </label>

            <span className="cc-form-hint" style={{ flex: 1, minWidth: 220, alignSelf: "center" }}>
              Equal monthly invoices, each issued a week before its due date.
            </span>
          </div>

          <div className="cc-form-actions">
            {canEmail ? (
              <label className="cc-form-check">
                <input
                  type="checkbox"
                  checked={planNotify}
                  onChange={(e) => setPlanNotify(e.target.checked)}
                />
                Email each part to {clientEmail}
              </label>
            ) : (
              <span className="cc-form-hint">
                No email on file for this client - the parts are issued without one.
              </span>
            )}
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="cc-btn-primary"
              disabled={planMut.isPending}
              onClick={submitPlan}
            >
              {planMut.isPending ? "Creating..." : "Create plan"}
            </button>
          </div>

          {planError && (
            <div role="alert" className="cc-form-error">
              {planError}
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div style={{ padding: "8px 18px" }}>
          <SkeletonGroup label="Loading invoices" gap={8}>
            <Skeleton height={16} />
            <Skeleton height={16} width="70%" />
          </SkeletonGroup>
        </div>
      )}
      {isError && (
        <div style={{ padding: "8px 0", color: "var(--warn-600)" }}>
          Could not reach the server - invoices are unavailable.
        </div>
      )}
      {!isLoading && !isError && !formShown && !planShown && invoices.length === 0 && (
        <div
          style={{ padding: "8px 18px 12px", color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}
        >
          Nothing has been billed on this matter. An invoice is what money is recorded against - a
          card payment the client makes in their portal, or cash and cheques the office takes in -
          and the client sees it once their portal access is on.
          <br />
          {billingOn
            ? "Bill it in one go with an invoice, or set up a plan to bill it in instalments."
            : BILLING_OFF_COPY}
        </div>
      )}

      {invoices.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {invoices.map((inv) => (
            <li key={inv._id}>
              <div className="cc-money-row cc-money-cols-matter">
                <span className="amt">{fmtCents(inv.amountCents)}</span>
                <span style={{ fontSize: 13 }}>
                  <Link className="cc-link-subtle" to={`/billing/invoice/${inv._id}`}>
                    <span className="tnum-mono">{inv.number ?? ""}</span>
                    {inv.number ? " " : ""}
                    {inv.description}
                  </Link>
                  {inv.dueDate ? (
                    <>
                      {" (due "}
                      <span className="tnum">{day(inv.dueDate)}</span>
                      {")"}
                    </>
                  ) : (
                    ""
                  )}
                </span>
                <span className={`cc-pill ${INVOICE_PILL[inv.status]}`}>{inv.status}</span>
                <span className="actions">
                  <button type="button" className="cc-link-btn" onClick={() => void savePdf(inv)}>
                    PDF
                  </button>
                  {inv.status === "open" && isAdmin && billingOn && (
                    <button
                      type="button"
                      className="cc-link-btn"
                      onClick={() => setRecordForId((v) => (v === inv._id ? null : inv._id))}
                    >
                      {recordForId === inv._id ? "Close form" : "Record payment"}
                    </button>
                  )}
                  {inv.status === "open" && billingOn && (
                    <VoidInvoiceButton
                      invoiceId={inv._id}
                      caseId={caseId}
                      number={inv.number}
                      amountCents={inv.amountCents}
                      onVoided={() => {
                        setNote(null);
                        setActionError(null);
                      }}
                      onError={setActionError}
                    />
                  )}
                </span>
              </div>
              {/* Inside the bill's own row: with three on a matter, a form
                  floating under the list gives no clue which one it settles. */}
              {billingOn && recordForId === inv._id && inv.status === "open" && (
                <RecordPaymentForm
                  invoiceId={inv._id}
                  caseId={caseId}
                  number={inv.number}
                  amountCents={inv.amountCents}
                  onCancel={() => setRecordForId(null)}
                  onRecorded={(healed) => {
                    setRecordForId(null);
                    setActionError(null);
                    setNote(recordedNote(healed, inv.number, inv.amountCents));
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
